<template>
  <v-container fluid>
    <v-alert
      v-if="showLastErrorBanner"
      type="error"
      outlined
      class="mb-4"
    >
      <div class="font-weight-medium">
        Matching failed
      </div>
      <div class="text-body-2 mt-1">
        {{ batch.last_error }}
      </div>
      <div class="text-caption grey--text mt-2">
        You can also re-upload either bank's statement before retrying.
      </div>
      <v-btn
        color="error"
        depressed
        small
        class="mt-3"
        :loading="matchInFlight"
        :disabled="matchInFlight"
        @click="onRetryMatching"
      >
        Retry matching
      </v-btn>
    </v-alert>

    <div
      v-if="batch"
      class="d-flex align-center justify-space-between mb-4"
    >
      <div>
        <h2 class="text-h5">
          Bulk reconciliation
        </h2>
        <div class="text-body-2 grey--text">
          {{ batch.start_date }} → {{ batch.end_date }}
        </div>
      </div>
      <div
        class="d-flex align-center"
        style="gap: 12px;"
      >
        <v-chip
          small
          :color="statusChipColor"
          text-color="white"
        >
          {{ statusLabel }}
        </v-chip>
        <v-btn
          v-if="phase === 'result'"
          color="error"
          outlined
          small
          @click="showCloseDialog = true"
        >
          Close batch
        </v-btn>
      </div>
    </div>

    <div
      v-if="phase === 'loading'"
      class="text-center py-12"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <div
      v-else-if="phase === 'not_found'"
      class="text-center py-12"
    >
      <v-icon
        size="56"
        color="grey lighten-1"
      >
        mdi-database-off
      </v-icon>
      <div class="text-h6 mt-4">
        Batch not found
      </div>
      <div class="text-body-2 grey--text mt-1">
        This batch doesn't exist or you don't have access to it.
      </div>
      <v-btn
        color="primary"
        depressed
        class="mt-4"
        to="/eod/bulk-reconciliation"
      >
        Back to bulk reconciliation
      </v-btn>
    </div>

    <v-alert
      v-else-if="phase === 'error'"
      type="error"
      outlined
    >
      <div class="font-weight-medium">
        Failed to load batch
      </div>
      <div class="text-body-2 mt-1">
        {{ loadError.message }}
      </div>
      <v-btn
        color="error"
        depressed
        small
        class="mt-3"
        :loading="loadingInitial"
        :disabled="loadingInitial"
        @click="loadInitial"
      >
        Retry
      </v-btn>
    </v-alert>

    <div v-else-if="phase === 'upload'">
      <v-row dense>
        <v-col
          cols="12"
          md="6"
        >
          <bulk-upload-card
            bank="BCEL"
            accept=".xlsx"
            :existing-file="bcelExisting"
            :disabled="uploadInFlight.BCEL || matchInFlight"
            @upload="onUpload('BCEL', $event)"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <bulk-upload-card
            bank="LDB"
            accept=".xlsx,.pdf,.html"
            :existing-file="ldbExisting"
            :disabled="uploadInFlight.LDB || matchInFlight"
            @upload="onUpload('LDB', $event)"
          />
        </v-col>
      </v-row>
      <div class="text-center mt-6">
        <v-btn
          color="primary"
          depressed
          large
          :disabled="!canRunMatching"
          :loading="matchInFlight"
          @click="onRunMatching"
        >
          Run matching
        </v-btn>
        <div
          v-if="!canRunMatching && !matchInFlight"
          class="text-caption grey--text mt-2"
        >
          {{ runMatchingHint }}
        </div>
      </div>
    </div>

    <div
      v-else-if="phase === 'matching'"
      class="text-center py-12"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="48"
      />
      <div class="text-body-1 mt-4">
        Matching in progress…
      </div>
      <div
        v-if="matchingElapsedLabel"
        class="text-caption grey--text"
      >
        {{ matchingElapsedLabel }}
      </div>
      <div
        v-if="matchingElapsedMs > 5 * 60 * 1000"
        class="text-caption grey--text mt-2"
      >
        Long-running matches normally complete within 10 minutes; refresh if needed.
      </div>
    </div>

    <div v-else-if="phase === 'result' || phase === 'closed'">
      <bulk-summary-strip
        :uuid="uuid"
        :batch="batch"
        :refetch-key="recordsRefetchKey"
        class="mb-4"
      />
      <bulk-result-tabs
        :uuid="uuid"
        :read-only="phase === 'closed'"
        :refetch-key="recordsRefetchKey"
        @link-bill="onLinkBillRequest"
      />
    </div>

    <bulk-link-bill-dialog
      v-model="showLinkDialog"
      :uuid="uuid"
      :record="linkTargetRecord"
      @linked="onLinked"
    />

    <bulk-close-confirm-dialog
      v-model="showCloseDialog"
      :submitting="closeInFlight"
      @confirm="onConfirmClose"
    />
  </v-container>
</template>

<script>
import startPolling from '@/utils/poll';
import {
  getBatch, runMatching, closeBatch, uploadStatement,
} from '@/store/apis/bulkReconciliation';
import BulkCloseConfirmDialog from '@/views/eod/components/bulk/BulkCloseConfirmDialog.vue';
import BulkUploadCard from '@/views/eod/components/bulk/BulkUploadCard.vue';
import BulkSummaryStrip from '@/views/eod/components/bulk/BulkSummaryStrip.vue';
import BulkResultTabs from '@/views/eod/components/bulk/BulkResultTabs.vue';
import BulkLinkBillDialog from '@/views/eod/components/bulk/BulkLinkBillDialog.vue';

const STATUS_LABELS = {
  draft: 'DRAFT',
  uploading: 'UPLOADING',
  ready_to_match: 'READY TO MATCH',
  matching: 'MATCHING',
  matched: 'MATCHED',
  closed: 'CLOSED',
};

const STATUS_COLORS = {
  draft: 'grey',
  uploading: 'amber darken-1',
  ready_to_match: 'amber darken-2',
  matching: 'blue',
  matched: 'success',
  closed: 'green darken-2',
};

const POLLING_STATUSES = new Set(['uploading', 'matching']);

export default {
  name: 'BulkReconciliationDetail',
  components: {
    BulkCloseConfirmDialog, BulkUploadCard, BulkSummaryStrip, BulkResultTabs, BulkLinkBillDialog,
  },
  data() {
    return {
      batch: null,
      pollCancel: null,
      matchingStartedAt: null,
      matchingElapsedMs: 0,
      elapsedTimer: null,
      showCloseDialog: false,
      closeInFlight: false,
      matchInFlight: false,
      recordsRefetchKey: 0,
      linkTargetRecord: null,
      showLinkDialog: false,
      uploadInFlight: { BCEL: false, LDB: false },
      loadError: null,
      loadingInitial: false,
    };
  },
  computed: {
    uuid() {
      return this.$route.params.uuid;
    },
    phase() {
      if (!this.batch) {
        if (this.loadError?.is404) return 'not_found';
        if (this.loadError) return 'error';
        return 'loading';
      }
      if (this.batch.status === 'closed') return 'closed';
      if (this.batch.status === 'matched') return 'result';
      if (this.batch.status === 'matching') return 'matching';
      return 'upload';
    },
    statusLabel() {
      return STATUS_LABELS[this.batch?.status] || '—';
    },
    statusChipColor() {
      return STATUS_COLORS[this.batch?.status] || 'grey';
    },
    showLastErrorBanner() {
      return !!this.batch
        && this.batch.status === 'ready_to_match'
        && !!this.batch.last_error;
    },
    matchingElapsedLabel() {
      if (!this.matchingStartedAt) return '';
      const totalSec = Math.floor(this.matchingElapsedMs / 1000);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      return `${m}:${String(s).padStart(2, '0')} elapsed`;
    },
    bcelExisting() { return this.findSubFile('BCEL'); },
    ldbExisting() { return this.findSubFile('LDB'); },
    canRunMatching() {
      return !!this.bcelExisting
        && !!this.ldbExisting
        && this.batch?.status === 'ready_to_match'
        && !this.matchInFlight;
    },
    runMatchingHint() {
      if (!this.bcelExisting && !this.ldbExisting) return 'Upload BCEL and LDB statements to enable matching.';
      if (!this.bcelExisting) return 'Waiting on BCEL statement.';
      if (!this.ldbExisting) return 'Waiting on LDB statement.';
      return '';
    },
  },
  async created() {
    await this.loadInitial();
  },
  beforeDestroy() {
    this.stopPolling();
    this.stopElapsedTimer();
  },
  watch: {
    'batch.status': function onStatusChange(next) {
      if (POLLING_STATUSES.has(next)) {
        this.startPollingIfNeeded();
        if (next === 'matching' && !this.matchingStartedAt) {
          this.matchingStartedAt = Date.now();
          this.startElapsedTimer();
        }
      } else {
        this.stopPolling();
        this.matchingStartedAt = null;
        this.matchingElapsedMs = 0;
        this.stopElapsedTimer();
      }
    },
  },
  methods: {
    async loadInitial() {
      if (this.loadingInitial) return;
      this.loadingInitial = true;
      this.loadError = null;
      try {
        this.batch = await getBatch(this.uuid);
      } catch (err) {
        const status = err.response?.status;
        const is404 = status === 404;
        const msg = is404
          ? 'Batch not found.'
          : (err.response?.data?.message || 'Failed to load batch.');
        this.loadError = { is404, message: msg };
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      } finally {
        this.loadingInitial = false;
      }
    },
    startPollingIfNeeded() {
      if (this.pollCancel) return;
      this.pollCancel = startPolling({
        intervalMs: 3000,
        fn: async () => {
          const next = await getBatch(this.uuid);
          this.batch = next;
          if (!POLLING_STATUSES.has(next.status)) return { shouldStop: true };
          if (next.last_error) return { shouldStop: true };
          return {};
        },
        onMaxFailures: () => {
          this.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: 'Lost connection — refresh to resume.',
          });
        },
      });
    },
    stopPolling() {
      if (this.pollCancel) {
        this.pollCancel();
        this.pollCancel = null;
      }
    },
    startElapsedTimer() {
      this.stopElapsedTimer();
      this.elapsedTimer = setInterval(() => {
        if (this.matchingStartedAt) {
          this.matchingElapsedMs = Date.now() - this.matchingStartedAt;
        }
      }, 1000);
    },
    stopElapsedTimer() {
      if (this.elapsedTimer) {
        clearInterval(this.elapsedTimer);
        this.elapsedTimer = null;
      }
    },
    async onRetryMatching() {
      if (this.matchInFlight) return;
      this.matchInFlight = true;
      try {
        await runMatching(this.uuid);
        // Optimistic transition; polling will confirm.
        this.batch = { ...this.batch, status: 'matching', last_error: null };
      } catch (err) {
        const msg = err.response?.data?.message || 'Failed to retry matching.';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      } finally {
        this.matchInFlight = false;
      }
    },
    async onConfirmClose() {
      if (this.closeInFlight) return;
      this.closeInFlight = true;
      try {
        this.batch = await closeBatch(this.uuid);
        this.showCloseDialog = false;
      } catch (err) {
        const msg = err.response?.data?.message || 'Failed to close batch.';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      } finally {
        this.closeInFlight = false;
      }
    },
    findSubFile(bank) {
      const sub = (this.batch?.sub_reconciliations || []).find(
        (s) => s.bank?.toUpperCase() === bank,
      );
      if (!sub) return null;
      return {
        filename: sub.filename || sub.original_filename || `${bank} statement`,
        row_count: sub.total_rows ?? sub.row_count ?? null,
      };
    },
    async onUpload(bank, file) {
      if (this.uploadInFlight[bank]) return;
      this.uploadInFlight = { ...this.uploadInFlight, [bank]: true };
      try {
        this.batch = await uploadStatement(this.uuid, bank, file);
        this.$store.commit('Toast_State', {
          value: true, color: 'success', msg: `${bank} statement uploaded.`,
        });
      } catch (err) {
        const msg = err.response?.data?.message || `Failed to upload ${bank} statement.`;
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      } finally {
        this.uploadInFlight = { ...this.uploadInFlight, [bank]: false };
      }
    },
    async onRunMatching() {
      await this.onRetryMatching();
    },
    onLinkBillRequest(record) {
      if (this.phase === 'closed') return;
      this.linkTargetRecord = record;
      this.showLinkDialog = true;
    },
    async onLinked() {
      this.recordsRefetchKey += 1;
      try {
        this.batch = await getBatch(this.uuid);
      } catch (err) {
        // Non-fatal — child tabs will refetch on key bump.
      }
    },
  },
};
</script>
